import React, {useCallback, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import DataTable from 'react-data-table-component';
import {getReport, ReportFilterValues, ReportPayload} from '@app/services/reports';
import {getAllActiveFranchise} from '@app/services/franchise';
import KpiStrip from './components/KpiStrip';
import ReportFilters, {FranchiseOption} from './components/ReportFilters';
import {
  ChartCard,
  ReportHorizontalBarChart,
  ReportMultiSeriesChart
} from './components/ReportChart';
import useReportExport from './hooks/useReportExport';
import {reportTableStyles} from './components/tableStyles';
import {monthYear} from './components/labels';
import {findReportBySlug} from './reportCatalogue';

/**
 * PRT1 — Print Volume & Mix.
 *
 * Set work prints are dated by when the work was ASSIGNED, not when the sheet
 * came off the printer — the rule the live threshold code already applies.
 * Using the print timestamp instead would move 47% of rows into a different
 * month, so the note on the chart is not decoration.
 *
 * The table is the aggregate (franchise x subject x month), not the 87k raw
 * prints — nobody wants that in a spreadsheet.
 */

const SLUG = 'print-volume';
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({length: 5}, (_, i) => CURRENT_YEAR - i);

const number = (v: any) => new Intl.NumberFormat('en-GB').format(Number(v) || 0);

const PrintVolume = () => {
  const definition = findReportBySlug(SLUG);

  const [payload, setPayload] = useState<ReportPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [franchises, setFranchises] = useState<FranchiseOption[]>([]);

  const [filters, setFilters] = useState<ReportFilterValues>({
    mode: 'year',
    year: CURRENT_YEAR,
    period: 'full',
    page: 1,
    limit: 25
  });
  const [applied, setApplied] = useState<ReportFilterValues>(filters);

  const {exportToExcel, exporting} = useReportExport('Print Volume', {
    // Same column names as the table.
    headers: {prints: 'Worksheets', handbook: 'Handbooks', students: 'Students Printing'}
  });

  const load = useCallback(async (active: ReportFilterValues) => {
    setLoading(true);
    setError(null);
    const response = await getReport(SLUG, active);
    if (response && response.success) {
      setPayload(response);
    } else {
      setPayload(null);
      setError(response?.message || 'The report could not be loaded.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load(applied);
  }, [applied, load]);

  useEffect(() => {
    (async () => {
      const response = await getAllActiveFranchise();
      if (response?.success === true && Array.isArray(response.collections)) {
        setFranchises(
          response.collections.map((f: any) => ({value: f.id, label: f.name}))
        );
      }
    })();
  }, []);

  const collections = payload?.collections;
  const byPeriod = collections?.series?.by_period || [];
  const bySubject = collections?.series?.by_subject || [];
  const detail = collections?.detail;

  // A zero count reads as "---" so the non-zero figures stand out.
  const count = (value: any) => (Number(value) ? number(value) : '---');

  const columns = [
    {name: 'Franchise', selector: (row: any) => row.franchise || '---', wrap: true},
    {name: 'Subject', selector: (row: any) => row.subject || '---', width: '140px'},
    {name: 'Month', selector: (row: any) => monthYear(row.month) || '---', width: '140px'},
    // How many different students printed in this franchise, subject and month.
    {name: 'Students Printing', width: '150px', right: true, selector: (row: any) => count(row.students)},
    // "prints" is worksheets only (set work + adhoc); handbooks are counted apart.
    {name: 'Worksheets', width: '120px', right: true, selector: (row: any) => count(row.prints)},
    {name: 'Set work', width: '110px', right: true, selector: (row: any) => count(row.setwork)},
    {
      name: 'Adhoc',
      width: '100px',
      right: true,
      cell: (row: any) => (
        <span className={row.adhoc > 0 ? 'text-warning font-weight-bold' : ''}>
          {count(row.adhoc)}
        </span>
      )
    },
    {name: 'Handbooks', width: '120px', right: true, selector: (row: any) => count(row.handbook)}
  ];

  return (
    <div>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-7">
              <h1>{definition?.name || 'Print Volume & Mix'}</h1>
              <p className="text-muted mb-0">{definition?.question}</p>
            </div>
            <div className="col-sm-5">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item"><a href="/">Home</a></li>
                <li className="breadcrumb-item"><Link to="/reports">Reports</Link></li>
                <li className="breadcrumb-item active">Print Volume</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <ReportFilters
            values={filters}
            onChange={setFilters}
            onApply={() => setApplied({...filters, limit: applied.limit, page: 1})}
            onReset={(defaults) => {
              setFilters(defaults);
              setApplied(defaults);
            }}
            onExport={() => exportToExcel(payload, SLUG, applied)}
            exporting={exporting}
            franchises={franchises}
            years={YEARS}
            intervalLabel={collections?.filters_applied?.interval_label}
            show={{franchise: true, subject: true, printType: true, student: true}}
            disabled={loading}
          />

          {error && (
            <div className="alert alert-warning">
              <i className="fas fa-exclamation-triangle mr-2" />
              {error}
            </div>
          )}

          <KpiStrip kpis={collections?.kpis || []} loading={loading} />

          <div className="row">
            <div className="col-lg-8">
              <ChartCard
                title="Prints over time, by type"
                subtitle="Set work is dated by when the work was assigned, not when it was printed — the same rule the print-threshold warnings use"
                loading={loading}
                isEmpty={!byPeriod.length}>
                <ReportMultiSeriesChart
                  data={byPeriod}
                  xKey="bucket"
                  currency={false}
                  series={[
                    {key: 'setwork', name: 'Set work', stackId: 'p', colour: '#0F8F82'},
                    {key: 'adhoc', name: 'Adhoc', stackId: 'p', colour: '#C9873A'},
                    {key: 'handbook', name: 'Handbook', stackId: 'p', colour: '#8A5FA8'}
                  ]}
                />
              </ChartCard>
            </div>
            <div className="col-lg-4">
              <ChartCard
                title="Prints by subject"
                subtitle="Only subjects with activity are shown"
                loading={loading}
                isEmpty={!bySubject.length}>
                <ReportHorizontalBarChart
                  data={bySubject}
                  labelKey="subject"
                  valueKey="prints"
                  currency={false}
                  labelWidth={110}
                  colour="#3D6DB5"
                />
              </ChartCard>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <div>
                <h3 className="card-title float-none d-block text-capitalize">Prints by franchise, subject and month</h3>
                <div>
                  <small className="text-muted">
                    Aggregated — the underlying print records number in the tens of thousands
                  </small>
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              <DataTable
                columns={columns}
                customStyles={reportTableStyles}
                data={detail?.rows || []}
                progressPending={loading}
                pagination
                paginationServer
                paginationTotalRows={detail?.total || 0}
                paginationDefaultPage={applied.page || 1}
                paginationPerPage={applied.limit || 25}
                paginationRowsPerPageOptions={[25, 50, 100]}
                onChangePage={(page) => setApplied({...applied, page})}
                onChangeRowsPerPage={(limit, page) =>
                  setApplied({...applied, limit, page})
                }
                noDataComponent={
                  <div className="text-muted py-4">No prints match these filters.</div>
                }
                highlightOnHover
                dense
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrintVolume;
